/**
 * @description
 * ErrorFallback component for the MediConnect Healthcare Information System
 * Provides a user-friendly UI when an error occurs in the application.
 * 
 * Key features:
 * - Displays user-friendly error messages
 * - Provides options to retry or report the error
 * - Handles healthcare-specific error types with appropriate messaging
 * - HIPAA-compliant error display (no PHI in error messages)
 * 
 * @dependencies
 * - react: For component rendering
 * - ../../types/error-types: For healthcare error type handling
 */

import React from 'react';
import { HealthcareApiError } from '../../types/error-types';
import { AlertCircle, RefreshCw, Send, ShieldAlert, Lock } from 'lucide-react';

export interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

/**
 * Determines if the error is a healthcare API error
 */
const isHealthcareApiError = (error: Error): error is HealthcareApiError => {
  return error instanceof HealthcareApiError;
};

/**
 * Gets a user-friendly error message
 */
const getUserFriendlyMessage = (error: Error): string => {
  if (isHealthcareApiError(error)) {
    return error.getUserFriendlyMessage();
  }
  
  // Special handling for common JavaScript errors
  if (error.message && error.message.includes('is undefined')) {
    // For "filters is undefined" specifically - this appears to be a common error
    if (error.message.includes('filters is undefined')) {
      return 'The application encountered an issue loading filter options. Please refresh the page to try again.';
    }
    
    // For other "x is undefined" errors
    return 'The application encountered an issue loading required data. Please refresh the page to try again.';
  }
  
  // For standard errors, provide a generic message
  return error.message || 'An unexpected error occurred. Please try again.';
};

/**
 * ErrorFallback component that displays a user-friendly error message
 */
const ErrorFallback: React.FC<ErrorFallbackProps> = ({ 
  error, 
  resetErrorBoundary 
}) => {
  const isApiError = isHealthcareApiError(error);
  const errorMessage = getUserFriendlyMessage(error);
  
  // Determine if this is a specific type of error
  const isUndefinedError = error.message && error.message.includes('is undefined');
  
  // Determine if this is a critical error that requires reporting
  const isCriticalError = isApiError 
    ? (error as HealthcareApiError).statusCode >= 500 
    : !isUndefinedError; // Undefined errors are common, not necessarily critical
  
  // Determine if this is a security-related error
  const isSecurityError = isApiError && (error as HealthcareApiError).isAuthError();
  
  // Determine if this is a HIPAA-related error
  const isHipaaError = isApiError && (error as HealthcareApiError).isHipaaError();
  
  // Handle reporting the error
  const handleReportError = () => {
    // In a real implementation, this would send the error to a reporting service
    console.log('Reporting error:', error);
    alert('Thank you for reporting this issue. Our team has been notified.');
  };
  
  return (
    <div className="p-8 bg-white/70 backdrop-blur-xl rounded-3xl shadow-lg border border-slate-200/60 max-w-lg w-full mx-auto my-8 md:my-16">
      <div className="flex items-center mb-6">
        <div className={`p-4 rounded-2xl mr-5 shadow-sm ${
          isSecurityError 
            ? 'bg-amber-100 text-amber-600' 
            : isHipaaError 
              ? 'bg-blue-100 text-blue-600' 
              : 'bg-red-100 text-red-600'
        }`}>
          {isSecurityError ? <Lock size={28} /> : isHipaaError ? <ShieldAlert size={28} /> : <AlertCircle size={28} />}
        </div>
        <h2 className="text-2xl font-bold text-slate-800 tracking-tight">
          {isSecurityError 
            ? 'Access Restricted' 
            : isHipaaError 
              ? 'Protected Health Information' 
              : 'Something went wrong'}
        </h2>
      </div>
      
      <div className="mb-8">
        <p className="text-slate-600 leading-relaxed text-[15px]">
          {errorMessage}
        </p>
        
        {isHipaaError && (
          <div className="p-4 bg-blue-50/50 rounded-2xl border border-blue-100 mt-4 text-sm text-blue-800 space-y-2">
            <p>For security and privacy reasons, access to this information is restricted.</p>
            <p>If you believe you should have access, please contact your healthcare provider.</p>
          </div>
        )}
        
        {isSecurityError && (
          <div className="p-4 bg-amber-50/50 rounded-2xl border border-amber-100 mt-4 text-sm text-amber-800 space-y-2">
            <p>Your session may have expired or you may not have permission to access this resource.</p>
            <p>Please try logging in again or contact support if the problem persists.</p>
          </div>
        )}
      </div>
      
      <div className="flex flex-wrap gap-3">
        <button
          onClick={resetErrorBoundary}
          className="flex items-center px-5 py-2.5 bg-gradient-to-br from-[#3D7A74] to-[#2A5C58] text-white rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all font-medium text-sm shadow-sm"
        >
          <RefreshCw size={16} className="mr-2.5 opacity-90" />
          Try Again
        </button>
        
        {isCriticalError && (
          <button
            onClick={handleReportError}
            className="flex items-center px-5 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-colors font-medium text-sm shadow-sm"
          >
            <Send size={16} className="mr-2.5 opacity-70" />
            Report Issue
          </button>
        )}
      </div>
    </div>
  );
};

export default ErrorFallback; 